import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtDateTime } from "@/lib/api";
import { playDing } from "@/lib/ding";

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const lastUnread = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      if (lastUnread.current !== null && res.data.unread > lastUnread.current) playDing();
      lastUnread.current = res.data.unread;
      setData(res.data);
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    navigate(n.link || "/");
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-white/10 transition-colors" data-testid="notification-bell-button">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-[11px] font-bold grid place-items-center" data-testid="notification-unread-count">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-heading uppercase tracking-wider text-sm">Notifiche</span>
          {data.unread > 0 && (
            <button onClick={readAll} className="text-xs text-emerald-700 hover:underline" data-testid="notification-read-all-button">
              Segna tutte come lette
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="px-4 py-6 text-sm text-muted-foreground">Nessuna notifica</p>}
          {data.items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-slate-50 transition-colors ${n.letta ? "" : "bg-emerald-50/60"}`}
            >
              <div className="text-sm">{n.testo}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
